import { useState, useEffect } from 'react';

type UseColumnVisibilityProps = {
  orgId?: number;
  defaultVisibility: Record<string, boolean>;
  configKey: string;
};

export const useColumnVisibility = ({
  orgId,
  defaultVisibility,
  configKey,
}: UseColumnVisibilityProps) => {
  const TABLE_CONFIG_KEY = `${configKey}_${orgId || 'default'}`;

  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(() => {
    const stored = localStorage.getItem(TABLE_CONFIG_KEY);
    if (stored) {
      try {
        const parsed: unknown = JSON.parse(stored);
        if (
          typeof parsed === 'object' &&
          parsed !== null &&
          'columnVisibility' in parsed
        ) {
          return (parsed as { columnVisibility: Record<string, boolean> })
            .columnVisibility;
        }
        return defaultVisibility;
      } catch {
        return defaultVisibility;
      }
    }
    return defaultVisibility;
  });

  // Save to localStorage whenever columnVisibility changes
  useEffect(() => {
    if (sessionStorage.getItem('columnVisibilityChanged')) return;
    const configToSave = { columnVisibility };
    localStorage.setItem(TABLE_CONFIG_KEY, JSON.stringify(configToSave));
  }, [columnVisibility, TABLE_CONFIG_KEY]);

  // Reload when orgId changes
  useEffect(() => {
    if (orgId && sessionStorage.getItem('columnVisibilityChanged') === 'true') {
      const storedConfigKey = `${configKey}_${orgId}`;
      const storedConfig = localStorage.getItem(storedConfigKey);

      let newColumnVisibility = defaultVisibility;

      if (storedConfig) {
        try {
          const parsed: unknown = JSON.parse(storedConfig);
          if (
            typeof parsed === 'object' &&
            parsed !== null &&
            'columnVisibility' in parsed
          ) {
            newColumnVisibility = (
              parsed as { columnVisibility: Record<string, boolean> }
            ).columnVisibility;
          }
        } catch {
          // Keep default if parsing fails
        }
      }
      sessionStorage.removeItem('columnVisibilityChanged');
      // Resync from localStorage after an org switch, gated by the
      // 'columnVisibilityChanged' flag set elsewhere when the org changes.
      // This reads external storage for the new org and can't be derived
      // from render alone.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setColumnVisibility(newColumnVisibility);
    }
  }, [orgId, configKey, defaultVisibility]);

  return { columnVisibility, setColumnVisibility };
};
